import { Event } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { Profile } from '../types/chat';
import { logger } from '../lib/logger';

/**
 * ProfileResolver - Maps pubkey to user profiles
 * Caches profiles to avoid repeated relay queries
 */
class ProfileResolver {
    private memoryCache: Map<string, Profile> = new Map();
    private pendingRequests: Map<string, Promise<Profile>> = new Map();
    private readonly CACHE_TTL = 3600000; // 1 hour in ms
    private cacheTimestamps: Map<string, number> = new Map();

    /**
     * Resolve a single profile
     */
    async resolveProfile(pubkey: string): Promise<Profile> {
        // Check memory cache first
        if (this.isCacheValid(pubkey)) {
            const cached = this.memoryCache.get(pubkey)!;
            logger.debug('Profile cache hit', { pubkey });
            return cached;
        }

        // Check if request is already pending (deduplication)
        if (this.pendingRequests.has(pubkey)) {
            logger.debug('Joining pending profile request', { pubkey });
            return this.pendingRequests.get(pubkey)!;
        }

        // Fetch from relay
        const promise = this.fetchFromRelay(pubkey);
        this.pendingRequests.set(pubkey, promise);

        try {
            const profile = await promise;
            this.cacheProfile(pubkey, profile);
            return profile;
        } finally {
            this.pendingRequests.delete(pubkey);
        }
    }

    /**
     * Batch resolve multiple profiles
     */
    async batchResolve(pubkeys: string[]): Promise<Map<string, Profile>> {
        const results = new Map<string, Profile>();
        const uniquePubkeys = [...new Set(pubkeys)];

        // Separate cached and non-cached
        const toFetch: string[] = [];
        for (const pubkey of uniquePubkeys) {
            if (this.isCacheValid(pubkey)) {
                results.set(pubkey, this.memoryCache.get(pubkey)!);
            } else {
                toFetch.push(pubkey);
            }
        }

        if (toFetch.length === 0) {
            return results;
        }

        // Batch fetch from relay
        const pool = getNostrPool();
        const events = await pool.querySync(RELAYS, {
            kinds: [0],
            authors: toFetch,
        });

        // Process results
        const profileMap = new Map<string, Event>();
        for (const event of events) {
            // Keep only the latest profile for each pubkey
            const existing = profileMap.get(event.pubkey);
            if (!existing || event.created_at > existing.created_at) {
                profileMap.set(event.pubkey, event);
            }
        }

        // Parse and cache
        for (const [pubkey, event] of profileMap) {
            const profile = this.parseProfile(pubkey, event);
            this.cacheProfile(pubkey, profile);
            results.set(pubkey, profile);
        }

        // Add fallback for missing profiles
        for (const pubkey of toFetch) {
            if (!results.has(pubkey)) {
                const fallback = this.createFallbackProfile(pubkey);
                this.cacheProfile(pubkey, fallback);
                results.set(pubkey, fallback);
            }
        }

        logger.info('Batch resolved profiles', {
            requested: uniquePubkeys.length,
            cached: uniquePubkeys.length - toFetch.length,
            fetched: toFetch.length
        });

        return results;
    }

    /**
     * Fetch single profile from relay
     */
    private async fetchFromRelay(pubkey: string): Promise<Profile> {
        const pool = getNostrPool();

        try {
            const events = await pool.querySync(RELAYS, {
                kinds: [0],
                authors: [pubkey],
                limit: 1,
            });

            if (events.length > 0) {
                // Get the latest profile
                const latestEvent = events.reduce((latest, event) =>
                    event.created_at > latest.created_at ? event : latest
                );

                return this.parseProfile(pubkey, latestEvent);
            }

            // No profile found
            logger.debug('No profile found, using fallback', { pubkey });
            return this.createFallbackProfile(pubkey);
        } catch (error) {
            logger.error('Failed to fetch profile', { error, pubkey });
            return this.createFallbackProfile(pubkey);
        }
    }

    /**
     * Parse profile metadata from event
     */
    private parseProfile(pubkey: string, event: Event): Profile {
        try {
            const metadata = JSON.parse(event.content);
            return {
                pubkey,
                name: metadata.name || metadata.display_name || this.truncatePubkey(pubkey),
                displayName: metadata.display_name,
                avatar: metadata.picture,
                about: metadata.about,
            };
        } catch (error) {
            logger.warn('Failed to parse profile metadata', { error, pubkey });
            return this.createFallbackProfile(pubkey);
        }
    }

    /**
     * Create fallback profile for unknown users
     */
    private createFallbackProfile(pubkey: string): Profile {
        return {
            pubkey,
            name: this.truncatePubkey(pubkey),
            avatar: undefined,
            about: undefined,
        };
    }

    /**
     * Truncate pubkey for display
     */
    private truncatePubkey(pubkey: string): string {
        return `${pubkey.slice(0, 8)}...${pubkey.slice(-4)}`;
    }

    /**
     * Check if cached profile is still valid
     */
    private isCacheValid(pubkey: string): boolean {
        if (!this.memoryCache.has(pubkey)) {
            return false;
        }

        const timestamp = this.cacheTimestamps.get(pubkey);
        if (!timestamp) {
            return false;
        }

        return Date.now() - timestamp < this.CACHE_TTL;
    }

    /**
     * Cache a profile
     */
    private cacheProfile(pubkey: string, profile: Profile): void {
        this.memoryCache.set(pubkey, profile);
        this.cacheTimestamps.set(pubkey, Date.now());
    }

    /**
     * Invalidate cache for a pubkey
     */
    invalidate(pubkey: string): void {
        this.memoryCache.delete(pubkey);
        this.cacheTimestamps.delete(pubkey);
        logger.debug('Profile cache invalidated', { pubkey });
    }

    /**
     * Clear all caches
     */
    clearAll(): void {
        this.memoryCache.clear();
        this.cacheTimestamps.clear();
        logger.info('Profile cache cleared');
    }
}

// Export singleton instance
export const profileResolver = new ProfileResolver();
